import type { ReactNode } from 'react';
import { Box, Stack, Text, ThemeIcon } from '@mantine/core';
import GrainMotif from './GrainMotif';

interface EmptyStateProps {
  /** Иконка-смысл, напр. <IconBuildingBank size={30} />. Цвет/фон задаёт ThemeIcon. */
  readonly icon: ReactNode;
  readonly title: string;
  readonly description?: string;
  /** Призыв к действию, обычно <Button> (напр. «Добавить счёт»). */
  readonly action?: ReactNode;
  /** Рисовать ли фоновый зерно-мотив. В «Купить»/«Продам» отключаем (decorated={false}). */
  readonly decorated?: boolean;
  /** Цвет кружка иконки. По умолчанию светло-зелёный `lime` (как основная кнопка); для «ошибочных»
   *  пусто-состояний — 'red'. Коричнево-пшеничный (wheat.*) НЕ использовать — см. docs/design-guidelines.md. */
  readonly color?: string;
}

// Единый «пустой» экран: иконка в кружке + (опц.) водяной зерно-мотив + заголовок +
// подсказка + действие, по центру. Применяется к спискам без данных. Хром (Card) — на стороне вызова.
export default function EmptyState({ icon, title, description, action, decorated = true, color = 'lime' }: EmptyStateProps) {
  return (
    <Box pos="relative" style={{ overflow: 'hidden' }}>
      {decorated ? (
        <>
          <GrainMotif
            variant="ear"
            size={220}
            color="var(--mantine-color-wheat-0)"
            style={{ right: -40, top: -30 }}
          />
          <GrainMotif
            variant="seed"
            size={160}
            color="var(--mantine-color-brand-0)"
            style={{ left: -50, bottom: -60 }}
          />
        </>
      ) : null}
      <Stack align="center" gap="xs" py="xl" px="md" pos="relative">
        <ThemeIcon size={64} radius="xl" variant="light" color={color}>
          {icon}
        </ThemeIcon>
        <Text fw={600} c="gray.8" size="lg" ta="center" mt="xs">
          {title}
        </Text>
        {description ? (
          <Text c="gray.7" size="sm" ta="center" maw={360}>
            {description}
          </Text>
        ) : null}
        {action ? <div style={{ marginTop: 'var(--mantine-spacing-xs)' }}>{action}</div> : null}
      </Stack>
    </Box>
  );
}
